import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import Button from '../../components/Button/Button.component';
import { Column, Row, Wrap } from '../../assets/styledAssets/grid';
import {
  accentColorPrim,
  fontColorSecond,
  red,
} from '../../assets/styledAssets/vars';
import { updateProfileAction } from './Profile.actions';
import Moment from 'react-moment';
import messages from './messages';
import {
  StyledFormField,
  StyledLabel,
  StyledRadioGroup,
} from '../../components/FormField/styles';
import { Input, RadioButtonProps } from '@progress/kendo-react-inputs';
import { Gender, LeftRight, User } from '../../domains/app';
import moment from 'moment';
import { StyledImgWrap, StyledWrap, SyledButton } from './styles';
import { DeleteIcon } from '../../components/Icons/Icons.component';
import {
  Upload,
  UploadOnAddEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import { baseUrl } from '../../app/constants';
import {
  authProvider,
  authProviderDefault,
  config,
} from '../../providers/auth-provider';
import { avatarLink } from '../../helpers/app.helpers';
import { useSnackbar } from 'notistack';
import { DateInput } from '@progress/kendo-react-dateinputs';

const connector = connect(
  (state: RootState) => ({
    user: state.app.user,
  }),
  {
    updateProfileAction,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Profile: FunctionComponent<PropsFromRedux> = ({
  user,
  updateProfileAction,
}) => {
  const intl = useIntl();
  const snackbar = useSnackbar();

  const [profileData, setProfileData] = useState<User | any>(user);
  const uploadRef = useRef<any>(null);

  const genderOptions: Array<RadioButtonProps> = useMemo(
    () => [
      {
        label: intl.formatMessage(messages.male),
        value: Gender.Male,
      },
      {
        label: intl.formatMessage(messages.female),
        value: Gender.Female,
      },
    ],
    // eslint-disable-next-line
    [],
  );

  const leftRigthOptions: Array<RadioButtonProps> = useMemo(
    () => [
      {
        label: intl.formatMessage(messages.left),
        value: LeftRight.Left,
      },
      {
        label: intl.formatMessage(messages.right),
        value: LeftRight.Rigth,
      },
    ],
    // eslint-disable-next-line
    [],
  );

  const bmi = useMemo(() => {
    return (
      Math.round(
        (profileData.bodyWeight / (profileData.height / 100) ** 2) * 10,
      ) / 10 || 0
    );
  }, [profileData.bodyWeight, profileData.height]);

  const age = useMemo(() => {
    const ageDuration = moment.duration(
      moment().diff(moment(profileData.birthDate)),
    );
    return ageDuration.years();
  }, [profileData.birthDate]);

  useEffect(() => {
    convertToLbl(profileData.bodyWeight);
    convertToInc(profileData.height);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (profileData.bodyWeight !== user?.bodyWeight) {
      setProfileData({ ...profileData, bodyWeightUpdated: new Date() });
    }
    // eslint-disable-next-line
  }, [profileData.bodyWeight, profileData.height]);

  const convertToLbl = useCallback(
    (weight: number) => {
      const toLbl = Math.round(weight * 2.2 * 10) / 10 || 0;
      setProfileData((data: any) => ({
        ...data,
        bodyWeightLbl: toLbl,
        bodyWeight: weight,
      }));
    },
    // eslint-disable-next-line
    [
      profileData.bodyWeight,
      profileData.bodyWeightLbl,
      profileData.height,
      profileData.heightInc,
    ],
  );

  const convertToKg = useCallback(
    (weight: number) => {
      const toKg = Math.round((weight / 2.2) * 10) / 10 || 0;
      setProfileData((data: any) => ({
        ...data,
        bodyWeight: toKg,
        bodyWeightLbl: weight,
      }));
    },
    // eslint-disable-next-line
    [
      profileData.bodyWeight,
      profileData.bodyWeightLbl,
      profileData.height,
      profileData.heightInc,
    ],
  );

  const convertToInc = useCallback(
    (height: number) => {
      const toInc = Math.round((height / 2.54) * 10) / 10 || 0;
      setProfileData((data: any) => ({
        ...data,
        heightInc: toInc,
        height: height,
      }));
    },
    // eslint-disable-next-line
    [
      profileData.bodyWeight,
      profileData.bodyWeightLbl,
      profileData.height,
      profileData.heightInc,
    ],
  );

  const convertToCm = useCallback(
    (height: number) => {
      const toCm = Math.round(height * 2.54 * 10) / 10 || 0;
      setProfileData((data: any) => ({
        ...data,
        height: toCm,
        heightInc: height,
      }));
    },
    // eslint-disable-next-line
    [
      profileData.bodyWeight,
      profileData.bodyWeightLbl,
      profileData.height,
      profileData.heightInc,
    ],
  );

  const handleBirthChange = (value: Date) => {
    setProfileData({ ...profileData, birthDate: value });
  };

  const handleUserUpdate = useCallback(
    (usr: User) => {
      updateProfileAction(usr);
    },
    [updateProfileAction],
  );

  const onBeforeUpload = (event: any) => {
    const token: any = authProviderDefault.getAccountInfo();
    event.headers.Authorization = `Bearer ${token.jwtAccessToken}`;
  };
  const onUploadSuccess = (event: UploadOnStatusChangeEvent) => {
    setProfileData({ ...profileData, avatarId: event.response?.response });
  };

  const onAdd = (event: UploadOnAddEvent) => {
    if ((event.newState[0].size || 0) > 100000) {
      snackbar.enqueueSnackbar(intl.formatMessage(messages.maxSize), {
        variant: 'error',
      });
    }
  };

  const deleteAvatar = () => {
    setProfileData({ ...profileData, avatarId: undefined });
  };

  const openUpload = () => {
    uploadRef.current.actionElement.click();
  };

  const changePassword = () => {
    const provider = authProvider(config('b2c_1_reset'));
    provider.login();
  };

  return (
    <>
      <Wrap
        flexDir={'column'}
        overflow={'auto'}
        width={'100%'}
        flexWrap={'nowrap'}
      >
        <Wrap
          alignItems={'center'}
          justifyCont={'space-between'}
          width={'100%'}
          margin={'0 0 3rem 0'}
        >
          <Wrap
            fs={'3.2rem'}
            fw={'bold'}
            color={'#000'}
            alignItems={'flex-end'}
          >
            {intl.formatMessage(messages.header)}
          </Wrap>
          <Wrap justifyCont={'flex-end'}>
            <Button onClick={() => handleUserUpdate(profileData)} primary>
              {intl.formatMessage(messages.saveBtn)}
            </Button>
          </Wrap>
        </Wrap>
        <Wrap
          justifyCont={['flex-start', 'space-between']}
          grow={'1'}
          overflow={'auto'}
          width={'100%'}
          height={'100%'}
          flexDir={['column', 'row']}
        >
          <Column lg={40} xl={30}>
            <Row>
              <Wrap width={'100%'} margin={'0 0 3rem 0'} alignItems={'center'}>
                <StyledImgWrap
                  onClick={openUpload}
                  pic={avatarLink(profileData.avatarId)}
                />
                <Wrap
                  grow={'1'}
                  maxWidth={'calc(100% - 8rem)'}
                  padding={'0 0 0 2rem'}
                >
                  <SyledButton secondary onClick={openUpload}>
                    {intl.formatMessage(messages.uploadPhoto)}
                  </SyledButton>
                  <Upload
                    ref={(e) => (uploadRef.current = e)}
                    id={'upload-btn'}
                    className={'hidden'}
                    showActionButtons={false}
                    showFileList={false}
                    onBeforeUpload={onBeforeUpload}
                    onAdd={onAdd}
                    onStatusChange={onUploadSuccess}
                    saveUrl={`${baseUrl}api/Person/${profileData.id}/avatarImage`}
                  />
                  <Button icon onClick={deleteAvatar}>
                    <DeleteIcon width={'1.8rem'} color={red} />
                  </Button>
                </Wrap>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 2rem 0'}>
                <Row>
                  <Column xs={50}>
                    <StyledLabel>
                      {intl.formatMessage(messages.firstName)}
                    </StyledLabel>
                    <StyledFormField>
                      <Input
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            firstName: e.target.value as string,
                          })
                        }
                        value={profileData?.firstName}
                      />
                    </StyledFormField>
                  </Column>
                  <Column xs={50}>
                    <StyledLabel>
                      {intl.formatMessage(messages.lastName)}
                    </StyledLabel>
                    <StyledFormField>
                      <Input
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            lastName: e.target.value as string,
                          })
                        }
                        value={profileData?.lastName}
                      />
                    </StyledFormField>
                  </Column>
                </Row>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 3rem 0'}>
                <StyledLabel>
                  {intl.formatMessage(messages.country)}
                </StyledLabel>
                <StyledFormField>
                  <Input
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        country: e.target.value as string,
                      })
                    }
                    value={profileData?.country}
                  />
                </StyledFormField>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 3rem 0'}>
                <StyledLabel>{intl.formatMessage(messages.pid)}</StyledLabel>
                <StyledFormField>
                  <Input
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        pid: e.target.value as string,
                      })
                    }
                    value={profileData?.pid}
                  />
                </StyledFormField>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 3rem 0'}>
                <StyledLabel>{intl.formatMessage(messages.fid)}</StyledLabel>
                <StyledFormField>
                  <Input
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        fid: e.target.value as string,
                      })
                    }
                    value={profileData?.fid}
                  />
                </StyledFormField>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 3rem 0'}>
                <StyledLabel>{intl.formatMessage(messages.sport)}</StyledLabel>
                <StyledFormField>
                  <Input
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        sport: e.target.value as string,
                      })
                    }
                    value={profileData?.sport}
                  />
                </StyledFormField>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 3rem 0'}>
                <StyledLabel>
                  {intl.formatMessage(messages.position)}
                </StyledLabel>
                <StyledFormField>
                  <Input
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        position: e.target.value as string,
                      })
                    }
                    value={profileData?.position}
                  />
                </StyledFormField>
              </Wrap>
              <Wrap width={'100%'} alignItems={'center'} margin={'0 0 3rem 0'}>
                <Wrap
                  margin={'0 1.6rem 0 1rem'}
                  color={fontColorSecond}
                  fs={'1.4rem'}
                >
                  {intl.formatMessage(messages.sex)}
                </Wrap>
                <StyledRadioGroup
                  name={intl.formatMessage(messages.sex)}
                  data={genderOptions}
                  defaultValue={profileData.gender}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      gender: e.value as Gender,
                    })
                  }
                />
              </Wrap>
              <Wrap width={'100%'} flexDir={'column'} margin={'0 0 3rem 0'}>
                <StyledLabel>{intl.formatMessage(messages.birth)}</StyledLabel>
                <StyledWrap>
                  <StyledFormField>
                    <DateInput
                      value={
                        moment(profileData?.birthDate).isValid()
                          ? moment(profileData?.birthDate).toDate()
                          : profileData?.birthDate
                      }
                      onChange={(e) => handleBirthChange(e.value as Date)}
                      format={'dd/MM/yyyy'}
                    />
                  </StyledFormField>
                  <Wrap color={fontColorSecond} fs={'1.4rem'}>
                    {intl.formatMessage(messages.age)}: {age}
                  </Wrap>
                </StyledWrap>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 3rem 0'}>
                <StyledLabel>{intl.formatMessage(messages.email)}</StyledLabel>
                <StyledFormField>
                  <Input
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        email: e.target.value as string,
                      })
                    }
                    value={profileData?.email}
                  />
                </StyledFormField>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 3rem 0'}>
                <StyledLabel>
                  {intl.formatMessage(messages.mobile)} (
                  {intl.formatMessage(messages.opt)})
                </StyledLabel>
                <StyledFormField>
                  <Input
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        phone: e.target.value as string,
                      })
                    }
                    value={profileData?.phone}
                  />
                </StyledFormField>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 3rem 0'}>
                <Button primary onClick={changePassword}>
                  {intl.formatMessage(messages.changePass)}
                </Button>
              </Wrap>
            </Row>
          </Column>
          <Column lg={40} xl={55}>
            <Row>
              <Wrap
                margin={'3rem 0 0 0'}
                fw={'bold'}
                color={accentColorPrim}
                fs={'2.4rem'}
              >
                {intl.formatMessage(messages.details)}
              </Wrap>
              <Wrap width={'100%'} alignItems={'center'} margin={'2.5rem 0'}>
                <Wrap
                  margin={'0 1.6rem 0 1rem'}
                  color={fontColorSecond}
                  fs={'1.4rem'}
                >
                  {intl.formatMessage(messages.dominantArm)}
                </Wrap>
                <StyledRadioGroup
                  name={intl.formatMessage(messages.dominantArm)}
                  defaultValue={profileData.dominantArm}
                  data={leftRigthOptions}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      dominantArm: e.value as LeftRight,
                    })
                  }
                />
              </Wrap>
              <Wrap
                width={'100%'}
                alignItems={'center'}
                margin={'0 0 2.5rem 0'}
              >
                <Wrap
                  margin={'0 1.6rem 0 1rem'}
                  color={fontColorSecond}
                  fs={'1.4rem'}
                >
                  {intl.formatMessage(messages.dominantLeg)}
                </Wrap>
                <StyledRadioGroup
                  name={intl.formatMessage(messages.dominantLeg)}
                  data={leftRigthOptions}
                  defaultValue={profileData.dominantLeg}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      dominantLeg: e.value as LeftRight,
                    })
                  }
                />
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 2.5rem 0'}>
                <Wrap width={'100%'}>
                  <StyledLabel>
                    {intl.formatMessage(messages.bodyWeight)}
                  </StyledLabel>
                </Wrap>

                <Row>
                  <Column xs={50} sm={30}>
                    <StyledWrap>
                      <StyledFormField>
                        <Input
                          type={'number'}
                          onChange={(e) => {
                            convertToLbl(e.target.value as number);
                          }}
                          value={profileData?.bodyWeight}
                        />
                        <Wrap color={fontColorSecond} fs={'1.4rem'}>
                          {intl.formatMessage(messages.kg)}
                        </Wrap>
                      </StyledFormField>
                    </StyledWrap>
                  </Column>
                  <Column xs={50} sm={30}>
                    <StyledWrap>
                      <StyledFormField>
                        <Input
                          type={'number'}
                          onChange={(e) => {
                            convertToKg(e.target.value as number);
                          }}
                          value={profileData?.bodyWeightLbl}
                        />
                        <Wrap color={fontColorSecond} fs={'1.4rem'}>
                          {intl.formatMessage(messages.lbs)}
                        </Wrap>
                      </StyledFormField>
                    </StyledWrap>
                  </Column>
                  <Column sm={40} flexRow>
                    <Wrap
                      fs={'1.4rem'}
                      alignItems={'center'}
                      color={fontColorSecond}
                      margin={['1rem 0 0 0', '0']}
                    >
                      {intl.formatMessage(messages.updated)}: &nbsp;
                      <Moment format={'DD MMM YYYY'}>
                        {profileData?.bodyWeightUpdated}
                      </Moment>
                    </Wrap>
                  </Column>
                </Row>
              </Wrap>
              <Wrap width={'100%'} margin={'0 0 2.5rem 0'}>
                <Wrap width={'100%'}>
                  <StyledLabel>
                    {intl.formatMessage(messages.height)}
                  </StyledLabel>
                </Wrap>
                <Row>
                  <Column xs={50} sm={30}>
                    <StyledWrap>
                      <StyledFormField>
                        <Input
                          type={'number'}
                          onChange={(e) =>
                            convertToInc(e.target.value as number)
                          }
                          value={profileData?.height}
                        />
                        <Wrap color={fontColorSecond} fs={'1.4rem'}>
                          {intl.formatMessage(messages.cm)}
                        </Wrap>
                      </StyledFormField>
                    </StyledWrap>
                  </Column>
                  <Column xs={50} sm={30}>
                    <StyledWrap>
                      <StyledFormField>
                        <Input
                          type={'number'}
                          onChange={(e) =>
                            convertToCm(e.target.value as number)
                          }
                          value={profileData?.heightInc}
                        />
                        <Wrap color={fontColorSecond} fs={'1.4rem'}>
                          {intl.formatMessage(messages.inc)}
                        </Wrap>
                      </StyledFormField>
                    </StyledWrap>
                  </Column>
                  <Column sm={40} flexRow>
                    <Wrap
                      fs={'1.4rem'}
                      alignItems={'center'}
                      color={fontColorSecond}
                      margin={['1rem 0 0 0', '0']}
                    >
                      {bmi} {intl.formatMessage(messages.bmi)}
                    </Wrap>
                  </Column>
                </Row>
              </Wrap>
            </Row>
          </Column>
        </Wrap>
      </Wrap>
    </>
  );
};

export default connector(Profile);
